import { Vue } from 'nuxt-property-decorator';
import { axiosInstance } from '../util/axiosInitializer';
import {
  NoteSearchResult,
  SearchFilter,
  SearchResult
} from '../types/searchTypes';
import { NoteHighlight } from '../types/highlightContract';
import { SolrHighlightingDict, SolrRootObject } from '../types/solrProxy';
import makeStoreDebugAccessible from '../util/storeDebug';
import { Site } from '../types/initialData';
import clientSettingsStore from './clientSettings.store';
import { Search as SearchEndpoint } from '@/types/initialData';
import { AppConstants } from '@/types/appConstants';

export const MAX_ROW_COUNT = 25;
export const MAX_RECENT_SEARCHES = 10;

interface ISearchState {
  currentSearchResult: SearchResult[];
  recentSearches: string[];
  totalResults: number;
  currentPage: number;
  currentQuery: string;
  currentSearchTerm: string;
  metaDataFilter: SearchFilter;
  notesFilter: SearchFilter;
  textOnPagesFilter: SearchFilter;
  titlesFilter: SearchFilter;
}

function initialRecentSearches(): string[] {
  const storedSearches = localStorage.getItem(
    AppConstants.RECENT_SEARCHES_IDENTIFIER
  );
  if (storedSearches) {
    return JSON.parse(storedSearches);
  }
  return [];
}

const state: ISearchState = Vue.observable({
  currentSearchResult: [],
  recentSearches: initialRecentSearches(),
  totalResults: 0,
  currentPage: 0,
  currentQuery: '',
  currentSearchTerm: '',
  metaDataFilter: {
    title: 'labels.search.filters.metadata',
    key: 'metadata',
    enabled: false
  },
  notesFilter: {
    title: 'labels.search.filters.notes',
    key: 'notes',
    enabled: false
  },
  textOnPagesFilter: {
    title: 'labels.search.filters.content',
    key: 'content',
    enabled: false
  },
  titlesFilter: {
    title: 'labels.search.filters.headlines',
    key: 'titles',
    enabled: false
  }
});

class SearchStore {
  constructor() {
    makeStoreDebugAccessible('searchStore', state);
  }

  get currentSearchTerm(): string {
    return state.currentSearchTerm;
  }

  set currentSearchTerm(term: string) {
    state.currentSearchTerm = term;
  }

  // Filters for search
  // This is not an array, because we 'only' have 4 filters, but could/should be refactored
  get metaDataFilter(): SearchFilter {
    return state.metaDataFilter;
  }

  // We update this in SearchResultsFilters.vue, even though the linter show it is not being used
  set metaDataFilter(filter: SearchFilter) {
    state.metaDataFilter = filter;
  }

  get notesFilter(): SearchFilter {
    return state.notesFilter;
  }

  // We update this in SearchResultsFilters.vue, even though the linter show it is not being used
  set notesFilter(filter: SearchFilter) {
    state.notesFilter = filter;
  }

  get textOnPagesFilter(): SearchFilter {
    return state.textOnPagesFilter;
  }

  // We update this in SearchResultsFilters.vue, even though the linter show it is not being used
  set textOnPagesFilter(filter: SearchFilter) {
    state.textOnPagesFilter = filter;
  }

  get titlesFilter(): SearchFilter {
    return state.titlesFilter;
  }

  // We update this in SearchResultsFilters.vue, even though the linter show it is not being used
  set titlesFilter(filter: SearchFilter) {
    state.titlesFilter = filter;
  }
  // Filters for search END//

  get totalResults(): number {
    return state.totalResults;
  }

  get searchResults(): SearchResult[] {
    return state.currentSearchResult;
  }

  get recentlySearchedList(): string[] {
    return state.recentSearches;
  }

  set recentlySearchedList(recentSearches: string[]) {
    state.recentSearches = recentSearches;
  }

  get sortedResults(): SearchResult[] {
    if (!this.searchResults || this.searchResults.length === 0) {
      return [];
    }
    return this.searchResults.slice().sort(SearchStore.sortAfterR4);
  }

  get totalPagesAvailable(): number {
    return Math.ceil(state.totalResults / MAX_ROW_COUNT);
  }

  get currentPage(): number {
    return state.currentPage;
  }

  get hasMoreResults(): boolean {
    return (
      state.currentPage + 1 < this.totalPagesAvailable &&
      // ensure not every result is a note, because that would mean we have no way to guarantee more results from the backend
      !state.currentSearchResult.every((res) => res.type === 'tt_note')
    );
  }

  private static sortAfterR4(a: SearchResult, b: SearchResult) {
    // Sorting is done as specified in https://systime.jira.com/wiki/spaces/IBKNG/pages/1110671477/S+gning+i+iBog+live+search+m.+filtre
    // See R4
    // TODO revisit and ensure priorities and typenames are correct (check output from search)

    const aIsTitle = a.type === 'tt_title';
    const bIsTitle = b.type === 'tt_title';

    const aIsPage = a.type === 'tt_pages';
    const bIsPage = b.type === 'tt_pages';

    const aIsContent = a.type === 'tt_content';
    const bIsContent = b.type === 'tt_content';

    const aIsNote = a.type === 'tt_note';
    const bIsNote = b.type === 'tt_note';

    const aIsFile = a.type === 'tx_solr_file';
    const bIsFile = b.type === 'tx_solr_file';
    const sortByTitle = SearchStore.compareBooleans(aIsTitle, bIsTitle);
    if (sortByTitle) {
      return sortByTitle;
    }

    const sortByPage = SearchStore.compareBooleans(aIsPage, bIsPage);
    if (sortByPage) {
      return sortByPage;
    }

    const sortByContent = SearchStore.compareBooleans(aIsContent, bIsContent);
    if (sortByContent) {
      return sortByContent;
    }

    const sortByNote = SearchStore.compareBooleans(aIsNote, bIsNote);
    if (sortByNote) {
      return sortByNote;
    }

    return SearchStore.compareBooleans(aIsFile, bIsFile);
  }

  private static compareBooleans(a: boolean, b: boolean): number {
    return Number(a) - Number(b);
  }

  addSearchToRecent(recent: string, site: Site) {
    this.deleteRecentItemNoFetch(recent);
    state.recentSearches.unshift(recent);
    if (state.recentSearches.length > MAX_RECENT_SEARCHES) {
      state.recentSearches.pop();
    }

    clientSettingsStore.updateClientSettings(site);
  }

  // A separate delete function which doesn't call the update on api
  deleteRecentItemNoFetch(item: string) {
    const existingIndex = state.recentSearches.indexOf(item);

    if (existingIndex !== -1) {
      state.recentSearches.splice(existingIndex, 1);
    }
  }

  deleteRecentItem(item: string, site: Site) {
    const existingIndex = state.recentSearches.indexOf(item);

    if (existingIndex !== -1) {
      state.recentSearches.splice(existingIndex, 1);
    }

    clientSettingsStore.updateClientSettings(site);
  }

  async fetchSearchResult(
    searchString: string,
    searchEndpoint: SearchEndpoint,
    page: number = 0,
    userGroups: string,
    solrCore: string
  ): Promise<void> {
    const userGroupParam = `fq={!typo3access}-2,0,${userGroups}`;
    const siteSpecific = this.getCurrentSite(true);
    /* Highlight params to use
      'hl' => string 'true' (length=4)
      'hl.fl' => string 'content' (length=7)
      'hl.fragsize' => int 200
      'hl.simple.pre' => string '<span class="results-highlight">' (length=32)
      'hl.simple.post' => string '</span>' (length=7)
      'hl.tag.pre' => string '<span class="results-highlight">' (length=32)
      'hl.tag.post' => string '</span>' (length=7)
      'hl.useFastVectorHighlighter' => string 'true' (length=4)
    */
    // Defaults to danish core if null or undefined
    const coreToSearch = solrCore ?? 'core_da';

    // 'hl.fl' takes an array of strings, separated by comma
    const fieldsToHighlight: Array<string> = [
      'title',
      'content',
      'filedatatitle_textS',
      'filedatadescription_textS',
      'filedatacaption_textS',
      'filedatacredits_textS',
      'filedatasource_textS',
      'medialabels_textS',
      'flexformcaption_textS',
      'flexformcredits_textS',
      'flexformsource_textS',
      'imagecaption_textS'
    ];
    const highlightParams = `hl=true&hl.fl=${fieldsToHighlight.join(
      ','
    )}S&hl.fragsize=200&hl.tag.pre=<marker>&hl.tag.post=</marker>&hl.useFastVectorHighlighter=true`;

    const response = await axiosInstance.$get<SolrRootObject>(
      `${
        searchEndpoint.endpoint
      }solr/${coreToSearch}/select?indent=off&q=${encodeURIComponent(
        searchString
      )}&wt=json&rows=${MAX_ROW_COUNT}&start=${page}&${highlightParams}&${userGroupParam}&${siteSpecific}`,
      {
        withCredentials: false,
        headers: {
          Authorization: `Bearer ${searchEndpoint.hash}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    state.currentQuery = searchString;
    state.currentPage = response.response.start;
    state.totalResults = response.response.numFound;

    const results = response.response.docs.map((doc) => {
      return {
        title: doc.title,
        content: doc.content,
        url: doc.url,
        type: doc.type, // TODO: create and set a new type for headlines, if searchString exists in title
        teaser: doc.teaser,
        pagetitle_textS: doc.pagetitle_textS,
        fileReferenceUrl: doc.fileReferenceUrl,
        filedatatitle_textS: doc.filedatatitle_textS,
        filedatadescription_textS: doc.filedatadescription_textS,
        filedatacaption_textS: doc.filedatacaption_textS,
        filedatacredits_textS: doc.filedatacredits_textS,
        filedatasource_textS: doc.filedatasource_textS,
        medialabels_textS: doc.medialabels_textS,
        imagecaption_textS: doc.imagecaption_textS,
        flexformcaption_textS: doc.flexformcaption_textS,
        flexformcredits_textS: doc.flexformcredits_textS,
        flexformsource_textS: doc.flexformsource_textS,
        mod_siteTitle_stringS: doc.mod_siteTitle_stringS,
        mod_breadCrumb_stringS: doc.mod_breadCrumb_stringS,
        pid: doc.pid,
        uid: doc.uid,
        solrHighlightTitle: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'title'
        ),
        solrHighlightContent: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'content'
        ),
        solrHighlightFileDataTitleTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'filedatatitle_textS'
        ),
        solrHighlightFileDataDescriptionTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'filedatadescription_textS'
        ),
        solrHighlightFileDataCaptionTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'filedatacaption_textS'
        ),
        solrHighlightFileDataCreditsTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'filedatacredits_textS'
        ),
        solrHighlightFileDataSourceTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'filedatasource_textS'
        ),
        solrHighlightMediaLabelsTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'medialabels_textS'
        ),
        solrHighlightImageCaptionTextS: SearchStore.getHighlightContent(
          doc.id,
          response.highlighting,
          'imagecaption_textS'
        )
      };
    });

    if (page > 0) {
      // if we are requesting another page than "0", assume we are searching for the same thing and add it to the search result.
      // By doing this, we allow that the notes result will live on
      /* @ts-ignore */
      state.currentSearchResult.push(...results);
    } else {
      /* @ts-ignore */
      state.currentSearchResult = results;
    }
  }

  private static getHighlightContent(
    id: string,
    highlightingResult: SolrHighlightingDict,
    field: string
  ): Array<string> | null {
    if (highlightingResult && highlightingResult[id]) {
      const result: any = highlightingResult[id];
      return result[`${field}`];
    }

    return null;
  }

  // todo: could use a getter instead, as this does not change much.
  // todo: we could combine the note results when you get "searchresults"
  // todo: the order might change whenever the search results are mutated with a "show more"
  fetchNotesResult(searchString: string, notes: NoteHighlight[]): void {
    const result: SearchResult[] = notes
      .filter(
        (note) =>
          // Set strings to lowercase to ensure matches in search
          note.userComment
            ?.toLowerCase()
            .includes(searchString.toLowerCase()) ||
          note.selectedText?.toLowerCase().includes(searchString.toLowerCase())
      )
      .map((note) => {
        // Refactor NoteSearchResult to its own type, se we don't need to extend SearchResult and use its props
        /* @ts-ignore */
        const searchResult: NoteSearchResult = {
          title: note.selectedText,
          content: note.userComment ?? '',
          type: 'tt_note',
          url: '',
          highlight: note,
          solrHighlightContent: []
        };
        return searchResult;
      });

    if (result.length > 0) {
      state.currentSearchResult.push(...result);
    }

    // Handle setting the correct total results count
    if (result.length === state.currentSearchResult.length) {
      state.totalResults = result.length;
    } else if (result.length > 0 && state.totalResults > 1) {
      state.totalResults += result.length;
    }
  }

  makeQueryFromTextAndFilters(
    userQuery: string,
    textQuery: string,
    titleChecked: boolean,
    contentChecked: boolean,
    mediaChecked: boolean,
    notesChecked: boolean
  ): string | null {
    const onlyNotes =
      !titleChecked && !contentChecked && !mediaChecked && notesChecked;

    if (onlyNotes) {
      return null;
    }

    // Title is title of page, and content element titles
    const fluffyTitleFilter = `(title:(${userQuery}))`;
    const fluffyTitleFilterText = `(title:(${textQuery}))`;
    // pagetitle_textS is only page title. Used to narrow queries for content elements in a page.
    // const onlyPageTitleFilter = `(pagetitle_textS:(${userQuery}))`;
    // For specific searching a title
    const exactTitleFilter = `(titleExact:(${userQuery}))`;
    const exactTitleFilterText = `(titleExact:(${textQuery}))`;

    // Content of elements
    const contentFilter = `(content:(${userQuery}))`;
    const contentFilterText = `(content:(${textQuery}))`;
    // More specific for searching after exact content
    const exactContentFilter = `(contentExact:(${userQuery}))`;
    const exactContentFilterText = `(contentExact:(${textQuery}))`;

    // File Meta Data
    const mediaFilter =
      `( (filedatatitle_textS:(${userQuery}))^2.0` +
      ` OR (filedatadescription_textS:(${userQuery}))^1.0` +
      ` OR (filedatacaption_textS:(${userQuery}))^1.0` +
      ` OR (filedatacredits_textS:(${userQuery}))^1.0` +
      ` OR (filedatasource_textS:(${userQuery}))^1.0` +
      ` OR (medialabels_textS:(${userQuery}))^1.0` +
      ` OR (flexformcaption_textS:(${userQuery}))^1.0` +
      ` OR (flexformcredits_textS:(${userQuery}))^1.0` +
      ` OR (flexformsource_textS:(${userQuery}))^1.0` +
      ` OR (imagecaption_textS:(${userQuery}))^1.0)`;

    const mediaFilterText =
      `( (filedatatitle_textS:(${textQuery}))^2.0` +
      ` OR (filedatadescription_textS:(${textQuery}))^1.0` +
      ` OR (filedatacaption_textS:(${textQuery}))^1.0` +
      ` OR (filedatacredits_textS:(${textQuery}))^1.0` +
      ` OR (filedatasource_textS:(${textQuery}))^1.0` +
      ` OR (medialabels_textS:(${textQuery}))^1.0` +
      ` OR (flexformcaption_textS:(${textQuery}))^1.0` +
      ` OR (flexformcredits_textS:(${textQuery}))^1.0` +
      ` OR (flexformsource_textS:(${textQuery}))^1.0` +
      ` OR (imagecaption_textS:(${textQuery}))^1.0)`;

    // Ensure we search on the specific site
    const siteSpecific = this.getCurrentSite(false);
    const everywhere =
      titleChecked && contentChecked && mediaChecked && notesChecked;
    const onlyTitle = titleChecked && !contentChecked && !mediaChecked;
    const onlyContent = !titleChecked && contentChecked && !mediaChecked;
    const onlyMedia = !titleChecked && !contentChecked && mediaChecked;

    // Example Boosting query values:
    // title^5.0, titleExact^15.0, pagetitle_stringS^1.0

    // For now testing removal of onlyPageTitleFilter
    // const titleQuery = `(${fluffyTitleFilter}^10.0 OR ${onlyPageTitleFilter}^0.5 OR ${exactTitleFilter}^20.0)`;

    const titleQuery = `(${fluffyTitleFilter}^10.0 OR ${fluffyTitleFilterText}^5.0 OR ${exactTitleFilter}^20.0 OR ${exactTitleFilterText}^10.0)`;
    const contentQuery = `(${contentFilter}^2.0 OR ${contentFilterText}^1.0 OR ${exactContentFilter}^5.0 OR ${exactContentFilterText}^2.5)`;
    const mediaQuery = `(${mediaFilter} OR ${mediaFilterText})`;
    const defaultQuery = `(${userQuery})`;

    const everywhereQuery = `(${defaultQuery} OR ${titleQuery} OR ${contentQuery} OR ${mediaQuery})`;

    let combinationQuery = `(${defaultQuery}`;
    if (titleChecked) {
      combinationQuery += ` OR ${titleQuery}`;
    }
    if (contentChecked) {
      combinationQuery += ` OR ${contentQuery}`;
    }
    if (mediaChecked) {
      combinationQuery += ` OR ${mediaQuery}`;
    }
    combinationQuery += ')';

    const query = `${siteSpecific} AND ${
      everywhere
        ? everywhereQuery
        : onlyTitle
        ? titleQuery
        : onlyContent
        ? contentQuery
        : onlyMedia
        ? mediaQuery
        : combinationQuery
    }`;

    // console.log(query); only for dev purpose
    return query;
  }

  isDevMode(): boolean {
    return process.env.NODE_ENV === 'development';
  }

  clearSearchResults(): void {
    state.currentSearchResult = [];
  }

  getCurrentSite(isFQ: boolean): string {
    if (this.isDevMode()) {
      return isFQ === true
        ? `fq=site:dev-aldringensbiologi.systime.dk`
        : `(site:"dev-aldringensbiologi.systime.dk")`;
    }

    return isFQ === true
      ? `fq=site:${document.location.host}`
      : `(site:"${document.location.host}")`;
  }
}

export default new SearchStore();
